/**
 * <AccentHeadline text accent /> — reusable section-headline accent
 * wrapper (Feb 2026 launch-blocking Item 14).
 *
 * Wraps a case-sensitive substring of `text` in the shipped brand
 * accent token (`text-pp-orange`) — the SAME Tailwind class the hero
 * H1 accent uses on `Landing.jsx` (see the `data-testid=
 * "hero-headline-period"` span). Do NOT reach for a new className,
 * a new CSS var, or an inline hex — Family-L guards ban inline hex
 * everywhere, and any new token would drift from the shipped hero
 * accent.
 *
 * Contract:
 *   - `text`    — the full headline string, verbatim from the content
 *                 module (`ABOUT_MARKETPLACE.headline`, etc.).
 *   - `accent`  — the substring of `text` to render in the accent
 *                 color. MUST appear in `text` exactly once, else the
 *                 helper renders the headline unmodified (fail-safe:
 *                 no crash, headline still readable).
 *
 * The helper returns a React fragment (no wrapper element) so the
 * caller's `<h2>` / `<h3>` remains the accessible heading landmark
 * without any extra DOM.
 *
 * Guard 11 (`pages/AccentedHeadlines.test.jsx`) pins that every
 * enumerated section headline consumes this wrapper.
 */
import React from "react";

export function AccentHeadline({ text, accent }) {
  if (!accent || typeof text !== "string" || typeof accent !== "string") {
    return text || null;
  }
  const idx = text.indexOf(accent);
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const after = text.slice(idx + accent.length);
  return (
    <>
      {before}
      <span className="text-pp-orange" data-testid="accent-headline-span">
        {accent}
      </span>
      {after}
    </>
  );
}
